import { useEffect } from 'react'
import { TutaraCard, TutaraCardContent, TutaraCardHeader } from '@/components/tutara-ui/core/TutaraCard'
import { TutaraSkeleton } from '@/components/tutara-ui/core/TutaraSkeleton'
import { signalSlowNavigation, signalNavigationComplete } from '@/components/layout/PageLoadingBar'

// Mirror of DashboardPage's layout so the Suspense fallback matches the real
// page structure (same header + 3-col main + 1-col sidebar) — no layout jump
// when the lazy chunk resolves. Keep in sync with DashboardPage.tsx.
const BAR_HEIGHTS = ['h-10', 'h-16', 'h-8', 'h-20', 'h-12', 'h-14', 'h-16']

export function DashboardSkeleton() {
  useEffect(() => {
    // Match PageSkeleton: drive the PageLoadingBar while the chunk loads.
    signalSlowNavigation()
    return () => { signalNavigationComplete() }
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">

        {/* Header + Stats Strip */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="space-y-2">
            <TutaraSkeleton className="h-8 w-64" />
            <TutaraSkeleton className="h-5 w-72" />
          </div>
          <TutaraSkeleton className="h-11 w-40 shrink-0" />
        </div>

        <div className="grid xl:grid-cols-4 gap-6">
          {/* Main Content (3 cols) */}
          <div className="xl:col-span-3 space-y-6">

            {/* Continue Learning */}
            <TutaraCard>
              <TutaraCardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 space-y-3">
                    <TutaraSkeleton className="h-5 w-32" />
                    <TutaraSkeleton className="h-6 w-2/3" />
                    <TutaraSkeleton className="h-4 w-full" />
                    <TutaraSkeleton className="h-2 w-full" />
                  </div>
                  <TutaraSkeleton className="h-11 w-28 shrink-0" />
                </div>
              </TutaraCardContent>
            </TutaraCard>

            {/* Other in-progress course rows */}
            <div className="space-y-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <TutaraSkeleton key={i} className="h-12 w-full" />
              ))}
            </div>

            {/* Weekly Insights */}
            <TutaraCard>
              <TutaraCardHeader>
                <TutaraSkeleton className="h-5 w-36" />
              </TutaraCardHeader>
              <TutaraCardContent>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="space-y-2">
                      <TutaraSkeleton className="h-7 w-16" />
                      <TutaraSkeleton className="h-3 w-12" />
                    </div>
                  ))}
                </div>
                <div className="flex items-end justify-between gap-2 h-24">
                  {BAR_HEIGHTS.map((h, i) => (
                    <TutaraSkeleton key={i} className={`flex-1 ${h} rounded-t-md`} />
                  ))}
                </div>
              </TutaraCardContent>
            </TutaraCard>

            {/* Recent Activity */}
            <TutaraCard>
              <TutaraCardHeader>
                <TutaraSkeleton className="h-5 w-36" />
              </TutaraCardHeader>
              <TutaraCardContent>
                <div className="space-y-2 pt-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 py-2">
                      <TutaraSkeleton className="w-8 h-8 rounded-full shrink-0" />
                      <div className="flex-1 space-y-1.5">
                        <TutaraSkeleton className="h-3.5 w-3/4" />
                        <TutaraSkeleton className="h-3 w-1/3" />
                      </div>
                    </div>
                  ))}
                </div>
              </TutaraCardContent>
            </TutaraCard>
          </div>

          {/* Sidebar (1 col) */}
          <div className="space-y-6">

            {/* Career Goal */}
            <TutaraCard>
              <TutaraCardHeader>
                <TutaraSkeleton className="h-5 w-28" />
              </TutaraCardHeader>
              <TutaraCardContent>
                <div className="space-y-2">
                  <TutaraSkeleton className="h-5 w-3/4" />
                  <TutaraSkeleton className="h-3 w-1/2" />
                  <TutaraSkeleton className="h-2 w-full mt-2" />
                </div>
              </TutaraCardContent>
            </TutaraCard>

            {/* Today's Goal */}
            <TutaraCard>
              <TutaraCardHeader>
                <TutaraSkeleton className="h-5 w-28" />
              </TutaraCardHeader>
              <TutaraCardContent>
                <div className="text-center py-4 space-y-3">
                  <TutaraSkeleton className="h-9 w-24 mx-auto" />
                  <TutaraSkeleton className="h-4 w-32 mx-auto" />
                  <TutaraSkeleton className="h-3 w-full" />
                </div>
              </TutaraCardContent>
            </TutaraCard>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardSkeleton
